import {browserLocalStorage} from "../core/browserStorage";

/**
 * Which accounts have already had their verification mail sent automatically (ui#1458).
 *
 * Plain TypeScript — no React — because this is the WHOLE of "once". The callout asks our api for a
 * verification mail on first sign-in without a click (ui#1477); without a durable record, every reload
 * mails the user again until the api's budget starts refusing, and the user's inbox is the place that
 * finds out. Keyed by Firebase uid, so two accounts on one browser never borrow each other's send.
 *
 * Per-BROWSER rather than per-account-globally, and that is the owner's call (2026-08-23): a second
 * device sending one more mail is acceptable, and the api's per-account budget (5 an hour) is the
 * backstop against anything worse. Nothing here is a security control.
 *
 * TABS ARE THE SAME TRADE, considered rather than missed. Reads and writes here are synchronous, so
 * a remount or a double-effect within ONE tab cannot double-send — but two tabs reaching the effect
 * before either has written (a session restore reopening several) each see an empty registry and each
 * mail. Claiming across tabs would need a `BroadcastChannel` handshake, i.e. an async protocol with
 * its own failure modes, in front of a mail that is already bounded by the api's per-account budget and
 * by the automatic path declining to mail an address Firebase reports as verified. Accepted, on the
 * same reasoning the owner applied to devices.
 */

/** Versioned so a future shape change invalidates the old one instead of misreading it. */
export const AUTO_SEND_STORAGE_KEY = "emailVerification.autoSent.v1";

/**
 * How many accounts to remember. One browser realistically sees one or two; the cap exists so a
 * shared machine cannot grow this without bound, and the OLDEST entry is the one that goes — a
 * long-dormant account re-sending once is the cheapest possible mistake for this list to make.
 */
const MAX_REMEMBERED_ACCOUNTS = 20;

/**
 * The recorded account ids, or null when the stored value is unreadable.
 *
 * `null` is deliberately distinct from `[]`: an empty registry means "nobody has been mailed", while
 * an unreadable one — corrupt, wrongly shaped, or no storage to read at all — means "this build
 * cannot tell", and those must lead to opposite decisions.
 */
function storedAccounts(): string[] | null {
	const storage = browserLocalStorage();
	if (storage === null) {
		// No storage at all — a browser refusing site data, a sandboxed frame, the SSG prerender. We can
		// neither read the registry nor WRITE it, so "not sent yet" would auto-mail on every single page
		// load with nothing able to remember it did. Unreadable, same as a corrupt blob.
		return null;
	}
	const raw = storage.getItem(AUTO_SEND_STORAGE_KEY);
	if (raw === null) {
		return [];
	}
	try {
		const parsed: unknown = JSON.parse(raw);
		if (!Array.isArray(parsed) || parsed.some(entry => typeof entry !== "string")) {
			return null;
		}
		return parsed as string[];
	} catch {
		return null;
	}
}

/**
 * Whether this account has already been mailed automatically.
 *
 * FAIL SAFE means "do not mail again": an unreadable registry answers `true`. The cost of being
 * wrong that way is a user pressing a button; the cost of the other direction is mailing somebody on
 * every page load. A missing account id also answers `true` — a session with no uid cannot be
 * recorded, so it must not be auto-sent either or it would send on every mount forever.
 */
export function hasAutoSentFor(accountId: string | null): boolean {
	if (accountId === null || accountId === "") {
		return true;
	}
	const accounts = storedAccounts();
	return accounts === null || accounts.includes(accountId);
}

/**
 * Whether a send was POSITIVELY recorded for this account.
 *
 * The mirror image of {@link hasAutoSentFor}, and the two must not be confused. That one answers
 * "may we auto-send?" and fails safe towards `true` — an unreadable registry or a session with no
 * uid suppresses the mail. This one answers "has a mail actually gone out?", which decides whether
 * the control says "Verify email" or "Resend email", and a cannot-tell state must answer `false`:
 * offering to RESEND a mail that was never sent tells the user something untrue.
 */
export function hasRecordedSendFor(accountId: string | null): boolean {
	if (accountId === null || accountId === "") {
		return false;
	}
	return storedAccounts()?.includes(accountId) ?? false;
}

/** Records an automatic send. Idempotent; a no-op where there is no storage, or without a uid. */
export function recordAutoSentFor(accountId: string | null): void {
	if (accountId === null || accountId === "") {
		return;
	}
	const storage = browserLocalStorage();
	if (storage === null) {
		return;
	}
	const existing = storedAccounts() ?? [];
	if (existing.includes(accountId)) {
		return;
	}
	const next = [...existing, accountId].slice(-MAX_REMEMBERED_ACCOUNTS);
	storage.setItem(AUTO_SEND_STORAGE_KEY, JSON.stringify(next));
}
